import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { skillColor } from "@/lib/skills";
import { ArabicText } from "@/components/ArabicText";
import Quiz from "@/components/Quiz";
import { isFullyArabic } from "@/lib/script";

import ConfettiBurst from "@/components/ConfettiBurst";
import {
  CheckCircle2,
  XCircle,
  Lightbulb,
  Sparkles,
  Target,
  ArrowRight,
  FastForward,
  RotateCcw,
  BookOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { t } from "@/i18n";

/* Mêmes bornes que Quiz.jsx : l'écran de fin occupe exactement la place
   libre entre le header et la barre d'onglets, jamais plus. */
const NAV_TOP = "4rem";
const NAV_BOTTOM = "calc(3.4rem + env(safe-area-inset-bottom))";

/* Détection d'écriture arabe : active l'affichage riche de Quiz.jsx
   (dir="rtl", bouton de prononciation) comme en vocabulaire. */

function buildLessonQuestions(lesson, lessonId) {
  return (lesson.quiz || []).map((q, qi) => {
    const opts = q.options || [];

    let shuffledOptions = [...opts];
    let newAnswer = q.answer;

    if (opts.length > 0 && q.answer !== undefined) {
      // 1. On crée un tableau avec les numéros de position (ex: [0, 1, 2])
      let indices = opts.map((_, i) => i);

      // 2. On mélange mathématiquement ces numéros (Fisher-Yates)
      // Ceci garantit une probabilité parfaite (ex: 33.3% de chance pour chaque place)
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }

      // 3. On réorganise les textes selon les nouveaux numéros
      shuffledOptions = indices.map((i) => opts[i]);

      // 4. L'ancienne position de la bonne réponse se trouve maintenant à ce nouvel index
      newAnswer = indices.indexOf(q.answer);
    }

    return {
      ...q,
      options: shuffledOptions,
      answer: newAnswer,
      arabicPrompt: q.arabicPrompt ?? isFullyArabic(q.prompt),
      arabicOptions:
        q.arabicOptions ??
        (opts.length > 0 && opts.some((o) => isFullyArabic(o))),
      key: `${lessonId}#q${qi}`,
      skill: lesson.skill,
      ref: lessonId,
    };
  });
}

export default function LessonPage() {
  const { lessonId } = useParams();
  const [lesson, setLesson] = useState(null);
  const [phase, setPhase] = useState("read");
  const [result, setResult] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const nav = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    setPhase("read");
    setResult(null);
    setRetryKey(0);
    window.scrollTo(0, 0);
    api
      .get(`/content/lesson/${lessonId}`)
      .then((r) => setLesson(r.data))
      .catch(() => nav("/roadmap"));
  }, [lessonId]);

  // L'utilisation de useMemo empêche l'ordre de changer quand on clique
  const quiz = useMemo(() => {
    if (!lesson) return [];
    return buildLessonQuestions(lesson, lessonId);
  }, [lesson, lessonId, retryKey]);

  if (!lesson)
    return (
      <div className="text-muted-foreground">
        {t("lessoneditor.chargement")}
      </div>
    );
  const color = skillColor(lesson.skill);

  const startQuiz = () => {
    window.scrollTo(0, 0);
    setRetryKey((k) => k + 1);
    setResult(null);
    setPhase("quiz");
  };

  const onDone = async ({ correct, total, earned }) => {
    const score = Math.round((correct / total) * 100);
    let bonus = 0;
    try {
      const { data } = await api.post("/progress/lesson", {
        lesson_id: lessonId,
        score,
      });
      bonus = data?.bonus ?? (score >= 80 ? 30 : 0);
      window.dispatchEvent(
        new CustomEvent("arabdex:dinar-update", { detail: data }),
      );
    } catch {
      bonus = score >= 80 ? 30 : 0;
    }
    setResult({ correct, total, score, earned: earned ?? 0, bonus });
    window.scrollTo(0, 0);
    setPhase("done");
  };

  return (
    <div className="max-w-2xl mx-auto">
      {phase !== "done" && (
        <>
          <div className="text-xs font-medium mb-1" style={{ color }}>
            {lesson.theme}
          </div>
          <h1 className="font-head text-3xl font-bold mb-1">{lesson.title}</h1>
          <div className="text-muted-foreground mb-4">
            <span className="arabic text-lg" dir="rtl">
              {lesson.term}
            </span>{" "}
            · {lesson.translit}
          </div>
        </>
      )}

      {phase === "read" && (
        <div className="space-y-4">
          {lesson.skill === "alphabet" && (
            <div className="rounded-2xl border-2 border-primary/20 bg-primary/5 p-4">
              <div className="text-xs font-bold uppercase tracking-[0.16em] text-primary">
                {t("lessonpage.le_fil_pour_apprendre_a_lire")}
              </div>
              <div className="mt-2 grid gap-2 text-sm sm:grid-cols-4">
                <div>
                  <strong>1.</strong> {t("lessonpage.reconnaitre_la_forme")}
                </div>
                <div>
                  <strong>2.</strong> {t("lessonpage.apprendre_le_nom")}
                </div>
                <div>
                  <strong>3.</strong> {t("lessonpage.apprendre_le_son")}
                </div>
                <div>
                  <strong>4.</strong>{" "}
                  {t("lessonpage.ajouter_les_voyelles_et_lire")}
                </div>
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                {t("lessonpage.le_nom_sert_a_repondre_au")}
              </p>
            </div>
          )}
          {lesson.objective && (
            <div
              className="rounded-2xl p-4 flex gap-3"
              style={{ background: `${color}15` }}
            >
              <Target size={20} style={{ color }} className="shrink-0 mt-0.5" />
              <div>
                <div
                  className="text-xs font-semibold uppercase tracking-wide mb-0.5"
                  style={{ color }}
                >
                  {t("lessonpage.objectif_de_la_lecon")}
                </div>
                <div className="text-sm">{lesson.objective}</div>
              </div>
            </div>
          )}
          {lesson.blocks.map((b, i) => {
            if (b.type === "text")
              return (
                <p
                  key={i}
                  data-testid={`block-${i}`}
                  className="leading-relaxed"
                >
                  {b.value}
                </p>
              );
            if (b.type === "concept")
              return (
                <div
                  key={i}
                  data-testid={`block-${i}`}
                  className="rounded-2xl border-l-4 bg-card p-4 flex gap-3"
                  style={{ borderColor: color }}
                >
                  <Sparkles
                    size={18}
                    style={{ color }}
                    className="shrink-0 mt-0.5"
                  />
                  <p className="font-medium leading-relaxed">{b.value}</p>
                </div>
              );
            if (b.type === "tip")
              return (
                <div
                  key={i}
                  data-testid={`block-${i}`}
                  className="rounded-2xl bg-ocre/10 p-4 flex gap-3"
                >
                  <Lightbulb size={18} className="text-ocre shrink-0 mt-0.5" />
                  <p className="text-sm leading-relaxed">{b.value}</p>
                </div>
              );
            if (b.type === "example")
              return (
                <div
                  key={i}
                  data-testid={`block-${i}`}
                  className="rounded-2xl border bg-card p-4 flex items-center justify-between gap-4"
                >
                  <ArabicText size="text-4xl">{b.ar}</ArabicText>
                  <div className="text-right">
                    <div className="text-sm text-muted-foreground">
                      {b.translit}
                    </div>
                    <div className="font-medium">{b.fr}</div>
                    {b.note && (
                      <div className="text-xs text-muted-foreground mt-1 max-w-[16rem]">
                        {b.note}
                      </div>
                    )}
                  </div>
                </div>
              );
            return null;
          })}
          <button
            data-testid="start-quiz"
            onClick={startQuiz}
            className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-semibold hover:opacity-90 transition"
          >
            {t("lessonpage.j_ai_compris_verifier")}
            {quiz.length} {t("lessonpage.questions")}
          </button>
          {user?.role === "admin" && (
            <button
              data-testid="admin-skip"
              onClick={async () => {
                await api.post(`/admin/skip-lesson/${lessonId}`);
                nav(lesson.next ? `/learn/lesson/${lesson.next}` : "/roadmap");
              }}
              className="w-full py-2 rounded-xl border text-xs text-muted-foreground hover:bg-secondary flex items-center justify-center gap-1"
            >
              <FastForward size={13} />{" "}
              {t("lessonpage.passer_cette_lecon_admin")}
            </button>
          )}
        </div>
      )}

      {phase === "quiz" && (
        <Quiz
          key={retryKey}
          questions={quiz}
          onDone={onDone}
          allowSkip={user?.role === "admin"}
          rewardMode="normal"
          rewardAmount={1}
          showCelebration={false}
          title={lesson.title}
          onExit={() => {
            setPhase("read");
            window.scrollTo(0, 0);
          }}
        />
      )}

      {phase === "done" &&
        result &&
        (() => {
          const ok = result.score >= 80;
          const totalCoins = result.earned + result.bonus;
          return (
            <div
              className="fixed inset-x-0 z-30 bg-background flex flex-col md:static md:z-auto md:bg-transparent"
              style={{ top: NAV_TOP, bottom: NAV_BOTTOM }}
              data-testid="lesson-done"
            >
              <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3">
                <div
                  className={`relative overflow-hidden mx-auto w-full max-w-md rounded-3xl border-2 bg-card p-5 text-center ${ok ? "border-emerald-500/30" : "border-destructive/40"}`}
                >
                  {totalCoins > 0 && <ConfettiBurst />}

                  {ok ? (
                    <CheckCircle2 className="mx-auto mb-2 w-11 h-11 text-emerald-600" />
                  ) : (
                    <XCircle className="mx-auto mb-2 w-11 h-11 text-destructive" />
                  )}

                  <h2 className="font-head text-xl font-bold mb-3">
                    {ok
                      ? t("lessonpage.lecon_validee")
                      : t("lessonpage.lecon_non_validee")}
                  </h2>

                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>
                      {result.correct} {t("lessonpage.correctes")}
                    </span>
                    <span>
                      {result.total - result.correct} {t("lessonpage.ratees")}
                    </span>
                  </div>
                  <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-3">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${result.score}%`,
                        backgroundColor: ok
                          ? "#16A34A"
                          : "hsl(var(--destructive))",
                      }}
                    />
                  </div>

                  <div
                    className="font-head text-3xl font-bold leading-none mb-1"
                    style={{
                      color: ok ? "#16A34A" : "hsl(var(--destructive))",
                    }}
                  >
                    {result.score}%
                  </div>
                  <p className="text-xs text-muted-foreground mb-3">
                    {result.correct}/{result.total}{" "}
                    {t("interexerciserecap.bonnes_reponses")}
                  </p>

                  <div className="rounded-2xl bg-amber-500/10 py-3 mb-3">
                    <div className="font-head text-3xl font-extrabold text-amber-500 leading-none">
                      +{totalCoins} 🪙
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                      {result.earned} {t("lessonpage.pour_tes_bonnes_reponses")}
                      {result.bonus > 0
                        ? t("lessonpage.bonus_de_lecon_validee", {
                            bonus: result.bonus,
                          })
                        : t("lessonpage.bonus_de_30_a_partir_de")}
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground mb-4 leading-snug">
                    {ok
                      ? t("lessonpage.tu_maitrises_cette_notion_on_enchaine")
                      : t(
                          "lessonpage.les_questions_ratees_reviendront_en_revision",
                        )}
                  </p>

                  {lesson.next ? (
                    <button
                      data-testid="next-lesson"
                      onClick={() => nav(`/learn/lesson/${lesson.next}`)}
                      className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 mb-2"
                    >
                      {t("lessonpage.lecon_suivante")} <ArrowRight size={16} />
                    </button>
                  ) : (
                    <button
                      data-testid="next-lesson"
                      onClick={() => nav("/exam/A1")}
                      className="w-full py-3 rounded-2xl bg-primary text-primary-foreground font-bold mb-2"
                    >
                      {t("lessonpage.passer_l_examen_a1")}
                    </button>
                  )}

                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <button
                      data-testid="retry-lesson-quiz"
                      onClick={startQuiz}
                      className="py-2.5 rounded-2xl border font-semibold text-sm flex items-center justify-center gap-1.5"
                    >
                      <RotateCcw size={14} /> {t("lessonpage.le_quiz")}
                    </button>
                    <button
                      data-testid="retry-lesson"
                      onClick={() => {
                        setResult(null);
                        setPhase("read");
                        window.scrollTo(0, 0);
                      }}
                      className="py-2.5 rounded-2xl border font-semibold text-sm flex items-center justify-center gap-1.5"
                    >
                      <BookOpen size={14} /> {t("lessonpage.la_lecon")}
                    </button>
                  </div>

                  <button
                    data-testid="back-roadmap"
                    onClick={() => nav("/roadmap")}
                    className="w-full py-2 text-sm text-muted-foreground hover:underline"
                  >
                    {t("lessonpage.voir_le_parcours")}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
}
